import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, useSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const image1Def: SlotGroupDefinition = {
  label: 'Image 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select the strongest, most visually appealing standout photo that clearly showcases this community. Prefer high-quality interiors, exteriors, or amenity spaces; skip logos, floor plans, parking lots, utility areas, and other low-value shots.',
    context:
      'One of four images in the Home page gallery strip. Prefer different photos and different subjects across the four (avoid stacking the same amenity, e.g. three pool shots). If the gallery is small or strong options are limited, repeating a subject or photo is fine as a backup — prioritize quality over forced variety.',
  },
};

const image2Def: SlotGroupDefinition = {
  label: 'Image 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select the strongest, most visually appealing standout photo that clearly showcases this community. Prefer high-quality interiors, exteriors, or amenity spaces; skip logos, floor plans, parking lots, utility areas, and other low-value shots.',
    context:
      'One of four images in the Home page gallery strip. Prefer different photos and different subjects across the four (avoid stacking the same amenity, e.g. three pool shots). If the gallery is small or strong options are limited, repeating a subject or photo is fine as a backup — prioritize quality over forced variety.',
  },
};

const image3Def: SlotGroupDefinition = {
  label: 'Image 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select the strongest, most visually appealing standout photo that clearly showcases this community. Prefer high-quality interiors, exteriors, or amenity spaces; skip logos, floor plans, parking lots, utility areas, and other low-value shots.',
    context:
      'One of four images in the Home page gallery strip. Prefer different photos and different subjects across the four (avoid stacking the same amenity, e.g. three pool shots). If the gallery is small or strong options are limited, repeating a subject or photo is fine as a backup — prioritize quality over forced variety.',
  },
};

const image4Def: SlotGroupDefinition = {
  label: 'Image 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select the strongest, most visually appealing standout photo that clearly showcases this community. Prefer high-quality interiors, exteriors, or amenity spaces; skip logos, floor plans, parking lots, utility areas, and other low-value shots.',
    context:
      'One of four images in the Home page gallery strip. Prefer different photos and different subjects across the four (avoid stacking the same amenity, e.g. three pool shots). If the gallery is small or strong options are limited, repeating a subject or photo is fine as a backup — prioritize quality over forced variety.',
  },
};

export function GalleryStrip() {
  const img1 = useSlotGroup('gallerystrip.image1', image1Def);
  const img2 = useSlotGroup('gallerystrip.image2', image2Def);
  const img3 = useSlotGroup('gallerystrip.image3', image3Def);
  const img4 = useSlotGroup('gallerystrip.image4', image4Def);

  return (
    <section className="w-full" aria-label="Photo gallery">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 w-full">
        <EditableSlotGroup
          groupPath="gallerystrip.image1"
          definition={image1Def}
          className="relative aspect-[4/5] overflow-hidden"
        >
          {img1.image ? (
            <VibesitesImage
              url={img1.image}
              alt={img1.alt || 'Property photo'}
              sizes="(min-width: 768px) 25vw, 50vw"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
          )}
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="gallerystrip.image2"
          definition={image2Def}
          className="relative aspect-[4/5] overflow-hidden"
        >
          {img2.image ? (
            <VibesitesImage
              url={img2.image}
              alt={img2.alt || 'Property photo'}
              sizes="(min-width: 768px) 25vw, 50vw"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
          )}
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="gallerystrip.image3"
          definition={image3Def}
          className="relative aspect-[4/5] overflow-hidden"
        >
          {img3.image ? (
            <VibesitesImage
              url={img3.image}
              alt={img3.alt || 'Property photo'}
              sizes="(min-width: 768px) 25vw, 50vw"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
          )}
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="gallerystrip.image4"
          definition={image4Def}
          className="relative aspect-[4/5] overflow-hidden"
        >
          {img4.image ? (
            <VibesitesImage
              url={img4.image}
              alt={img4.alt || 'Property photo'}
              sizes="(min-width: 768px) 25vw, 50vw"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
          )}
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryStrip',
  displayName: 'Gallery Strip',
  tokens: {},
  slotGroups: {
    'gallerystrip.image1': image1Def,
    'gallerystrip.image2': image2Def,
    'gallerystrip.image3': image3Def,
    'gallerystrip.image4': image4Def,
  },
};

(GalleryStrip as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryStrip;
